/**
 * Currency System
 *
 * Defines 8 crafting currency types based on Turkish mythology themes.
 * Each currency transforms equipment items in specific ways.
 */

import { EquipmentItem, ItemRarity, ItemMod } from '@shared/types';
import {
  rollSingleMod,
  collectModGroups,
  rollModsForItem,
  getModNamePrefix,
  getModNameSuffix,
} from '@shared/items/ModifierPool';
import { getAllSets } from '@shared/items/SetItemSystem';

// ========================
// Currency Type Definitions
// ========================

export type CurrencyType =
  | 'ruh_tozu'        // Normal → Magic
  | 'tengri_ozu'      // Magic → Rare
  | 'erlik_tasi'      // Reroll Rare mods
  | 'arindirma_suyu'  // Remove all mods (→ Normal)
  | 'ruh_muhru'       // Add 1 mod to Rare
  | 'yikim_tozu'      // Remove 1 random mod
  | 'tamga_muhru'     // Rare → Set
  | 'tengri_duasi';   // Reroll Unique implicit

export type CurrencyRarity = 'common' | 'uncommon' | 'rare' | 'very_rare';

export interface CurrencyItemDef {
  id: CurrencyType;
  name: string;
  nameKey: string;
  description: string;
  descriptionKey: string;
  icon: string;
  color: string;
  rarity: CurrencyRarity;
  dropWeight: number;
  maxStack: number;
}

// ========================
// Currency Definitions
// ========================

export const CURRENCY_DEFINITIONS: Record<CurrencyType, CurrencyItemDef> = {
  ruh_tozu: {
    id: 'ruh_tozu',
    name: 'Ruh Tozu',
    nameKey: 'currency.ruh_tozu.name',
    description: 'Normal eşyayı Sihirli yapar (1 prefix + 1 suffix ekler)',
    descriptionKey: 'currency.ruh_tozu.description',
    icon: '🌀',
    color: '#88aaff',
    rarity: 'common',
    dropWeight: 100,
    maxStack: 99,
  },
  tengri_ozu: {
    id: 'tengri_ozu',
    name: 'Tengri Özü',
    nameKey: 'currency.tengri_ozu.name',
    description: 'Sihirli eşyayı Nadir yapar (eksik modları doldurur)',
    descriptionKey: 'currency.tengri_ozu.description',
    icon: '✨',
    color: '#ffdd44',
    rarity: 'uncommon',
    dropWeight: 50,
    maxStack: 50,
  },
  erlik_tasi: {
    id: 'erlik_tasi',
    name: 'Erlik Taşı',
    nameKey: 'currency.erlik_tasi.name',
    description: 'Nadir eşyanın tüm modlarını yeniden atar',
    descriptionKey: 'currency.erlik_tasi.description',
    icon: '💀',
    color: '#cc44ff',
    rarity: 'rare',
    dropWeight: 20,
    maxStack: 30,
  },
  arindirma_suyu: {
    id: 'arindirma_suyu',
    name: 'Arındırma Suyu',
    nameKey: 'currency.arindirma_suyu.name',
    description: 'Tüm modları siler, eşyayı Normal yapar',
    descriptionKey: 'currency.arindirma_suyu.description',
    icon: '💧',
    color: '#44ddff',
    rarity: 'uncommon',
    dropWeight: 50,
    maxStack: 50,
  },
  ruh_muhru: {
    id: 'ruh_muhru',
    name: 'Ruh Mührü',
    nameKey: 'currency.ruh_muhru.name',
    description: 'Nadir eşyaya 1 modifier ekler (boş slot varsa)',
    descriptionKey: 'currency.ruh_muhru.description',
    icon: '🔮',
    color: '#ff8844',
    rarity: 'rare',
    dropWeight: 20,
    maxStack: 30,
  },
  yikim_tozu: {
    id: 'yikim_tozu',
    name: 'Yıkım Tozu',
    nameKey: 'currency.yikim_tozu.name',
    description: 'Nadir eşyadan rastgele 1 modifier siler',
    descriptionKey: 'currency.yikim_tozu.description',
    icon: '🌑',
    color: '#ff4444',
    rarity: 'rare',
    dropWeight: 20,
    maxStack: 30,
  },
  tamga_muhru: {
    id: 'tamga_muhru',
    name: 'Tamga Mührü',
    nameKey: 'currency.tamga_muhru.name',
    description: 'Nadir eşyayı Set eşyasına dönüştürür (rastgele set atanır)',
    descriptionKey: 'currency.tamga_muhru.description',
    icon: '🏷️',
    color: '#44ff88',
    rarity: 'very_rare',
    dropWeight: 5,
    maxStack: 10,
  },
  tengri_duasi: {
    id: 'tengri_duasi',
    name: 'Tengri Duası',
    nameKey: 'currency.tengri_duasi.name',
    description: 'Efsanevi eşyanın implicit değerlerini yeniden atar',
    descriptionKey: 'currency.tengri_duasi.description',
    icon: '🙏',
    color: '#ffaa00',
    rarity: 'very_rare',
    dropWeight: 3,
    maxStack: 10,
  },
};

// ========================
// Currency Application Result
// ========================

export interface CurrencyResult {
  success: boolean;
  item?: EquipmentItem;
  message: string;
  messageKey?: string;
}

// ========================
// Max mod counts by rarity
// ========================

function getMaxPrefixes(rarity: ItemRarity): number {
  switch (rarity) {
    case 'normal': return 0;
    case 'magic': return 1;
    case 'rare': return 3;
    case 'set': return 3;
    case 'legendary': return 3;
    default: return 0;
  }
}

function getMaxSuffixes(rarity: ItemRarity): number {
  switch (rarity) {
    case 'normal': return 0;
    case 'magic': return 1;
    case 'rare': return 3;
    case 'set': return 3;
    case 'legendary': return 3;
    default: return 0;
  }
}

// ========================
// Name Regeneration
// ========================

function regenerateItemName(item: EquipmentItem): string {
  const bestPrefix = item.prefixes.length > 0
    ? item.prefixes.reduce((best, m) => m.value > best.value ? m : best)
    : null;
  const bestSuffix = item.suffixes.length > 0
    ? item.suffixes.reduce((best, m) => m.value > best.value ? m : best)
    : null;

  const prefix = bestPrefix ? getModNamePrefix(bestPrefix.type, bestPrefix.calcType) : '';
  const suffix = bestSuffix ? getModNameSuffix(bestSuffix.type, bestSuffix.calcType) : '';
  const baseName = item.baseName || item.name;

  return `${prefix ? prefix + ' ' : ''}${baseName}${suffix ? ' ' + suffix : ''}`;
}

// ========================
// Currency Application Logic
// ========================

/**
 * Apply a currency to an equipment item.
 * Returns the modified item on success, or null with an error message if invalid.
 */
export function applyCurrency(currency: CurrencyType, item: EquipmentItem): CurrencyResult {
  switch (currency) {
    case 'ruh_tozu':
      return applyRuhTozu(item);
    case 'tengri_ozu':
      return applyTengriOzu(item);
    case 'erlik_tasi':
      return applyErlikTasi(item);
    case 'arindirma_suyu':
      return applyArindirmaSuyu(item);
    case 'ruh_muhru':
      return applyRuhMuhru(item);
    case 'yikim_tozu':
      return applyYikimTozu(item);
    case 'tamga_muhru':
      return applyTamgaMuhru(item);
    case 'tengri_duasi':
      return applyTengriDuasi(item);
    default:
      return { success: false, message: 'Bilinmeyen currency türü' };
  }
}

/**
 * Ruh Tozu - Normal → Magic (adds 1 prefix + 1 suffix)
 */
function applyRuhTozu(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'normal') {
    return {
      success: false,
      message: 'Ruh Tozu sadece Normal eşyalara uygulanabilir',
      messageKey: 'currency.ruh_tozu.error',
    };
  }

  item.rarity = 'magic';
  const { prefixes, suffixes } = rollModsForItem(item, 1, 1, item.ilvl);
  item.prefixes = prefixes;
  item.suffixes = suffixes;
  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: 'Eşya Sihirli oldu!',
    messageKey: 'currency.ruh_tozu.success',
  };
}

/**
 * Tengri Özü - Magic → Rare (fills missing modifiers up to 2 prefix + 2 suffix)
 */
function applyTengriOzu(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'magic') {
    return {
      success: false,
      message: 'Tengri Özü sadece Sihirli eşyalara uygulanabilir',
      messageKey: 'currency.tengri_ozu.error',
    };
  }

  item.rarity = 'rare';
  const existingGroups = collectModGroups(item);

  // Fill up to 2 prefixes
  while (item.prefixes.length < 2) {
    const mod = rollSingleMod(item, 'prefix', existingGroups, item.ilvl);
    if (!mod) break;
    item.prefixes.push(mod);
  }

  // Fill up to 2 suffixes
  while (item.suffixes.length < 2) {
    const mod = rollSingleMod(item, 'suffix', existingGroups, item.ilvl);
    if (!mod) break;
    item.suffixes.push(mod);
  }

  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: 'Eşya Nadir oldu!',
    messageKey: 'currency.tengri_ozu.success',
  };
}

/**
 * Erlik Taşı - Reroll all modifiers on a Rare item
 */
function applyErlikTasi(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'rare') {
    return {
      success: false,
      message: 'Erlik Taşı sadece Nadir eşyalara uygulanabilir',
      messageKey: 'currency.erlik_tasi.error',
    };
  }

  // Reroll with random counts: 1-3 prefixes, 1-3 suffixes
  const prefixCount = 1 + Math.floor(Math.random() * 3);
  const suffixCount = 1 + Math.floor(Math.random() * 3);

  const { prefixes, suffixes } = rollModsForItem(item, prefixCount, suffixCount, item.ilvl);
  item.prefixes = prefixes;
  item.suffixes = suffixes;
  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: 'Modlar yeniden atıldı!',
    messageKey: 'currency.erlik_tasi.success',
  };
}

/**
 * Arındırma Suyu - Remove all mods, revert to Normal
 */
function applyArindirmaSuyu(item: EquipmentItem): CurrencyResult {
  if (item.rarity === 'normal') {
    return {
      success: false,
      message: 'Arındırma Suyu Normal eşyalara uygulanamaz',
      messageKey: 'currency.arindirma_suyu.error_normal',
    };
  }
  if (item.rarity === 'legendary') {
    return {
      success: false,
      message: 'Arındırma Suyu Efsanevi eşyalara uygulanamaz',
      messageKey: 'currency.arindirma_suyu.error_legendary',
    };
  }

  item.rarity = 'normal';
  item.prefixes = [];
  item.suffixes = [];
  item.setId = undefined;
  item.name = item.baseName || item.name;

  return {
    success: true,
    item,
    message: 'Tüm modlar silindi, eşya Normal oldu!',
    messageKey: 'currency.arindirma_suyu.success',
  };
}

/**
 * Ruh Mührü - Add 1 modifier to a Rare item (if there's an open prefix or suffix slot)
 */
function applyRuhMuhru(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'rare') {
    return {
      success: false,
      message: 'Ruh Mührü sadece Nadir eşyalara uygulanabilir',
      messageKey: 'currency.ruh_muhru.error',
    };
  }

  const maxPfx = getMaxPrefixes(item.rarity);
  const maxSfx = getMaxSuffixes(item.rarity);
  const canAddPrefix = item.prefixes.length < maxPfx;
  const canAddSuffix = item.suffixes.length < maxSfx;

  if (!canAddPrefix && !canAddSuffix) {
    return {
      success: false,
      message: 'Eşyada boş modifier yuvası yok',
      messageKey: 'currency.ruh_muhru.error_full',
    };
  }

  const existingGroups = collectModGroups(item);

  // Randomly pick prefix or suffix (weighted by available slots)
  let pool: 'prefix' | 'suffix';
  if (canAddPrefix && canAddSuffix) {
    pool = Math.random() < 0.5 ? 'prefix' : 'suffix';
  } else {
    pool = canAddPrefix ? 'prefix' : 'suffix';
  }

  const mod = rollSingleMod(item, pool, existingGroups, item.ilvl);
  if (mod) {
    if (pool === 'prefix') {
      item.prefixes.push(mod);
    } else {
      item.suffixes.push(mod);
    }
  }

  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: `Yeni ${pool === 'prefix' ? 'prefix' : 'suffix'} eklendi!`,
    messageKey: 'currency.ruh_muhru.success',
  };
}

/**
 * Yıkım Tozu - Remove 1 random modifier from a Rare item
 */
function applyYikimTozu(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'rare') {
    return {
      success: false,
      message: 'Yıkım Tozu sadece Nadir eşyalara uygulanabilir',
      messageKey: 'currency.yikim_tozu.error',
    };
  }

  const totalMods = item.prefixes.length + item.suffixes.length;
  if (totalMods === 0) {
    return {
      success: false,
      message: 'Silinecek modifier yok',
      messageKey: 'currency.yikim_tozu.error_empty',
    };
  }

  // Weighted random: pick prefix or suffix proportional to count
  const removePrefix = Math.random() < (item.prefixes.length / totalMods);
  if (removePrefix && item.prefixes.length > 0) {
    const idx = Math.floor(Math.random() * item.prefixes.length);
    item.prefixes.splice(idx, 1);
  } else if (item.suffixes.length > 0) {
    const idx = Math.floor(Math.random() * item.suffixes.length);
    item.suffixes.splice(idx, 1);
  }

  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: 'Rastgele bir modifier silindi!',
    messageKey: 'currency.yikim_tozu.success',
  };
}

/**
 * Tamga Mührü - Rare → Set item (assigns a random set)
 */
function applyTamgaMuhru(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'rare') {
    return {
      success: false,
      message: 'Tamga Mührü sadece Nadir eşyalara uygulanabilir',
      messageKey: 'currency.tamga_muhru.error',
    };
  }

  const allSets = getAllSets();
  if (allSets.length === 0) {
    return {
      success: false,
      message: 'Uygun set bulunamadı',
      messageKey: 'currency.tamga_muhru.error_no_sets',
    };
  }

  // Pick a random set
  const randomSet = allSets[Math.floor(Math.random() * allSets.length)];

  item.rarity = 'set';
  item.setId = randomSet.id;

  // Keep existing mods but cap at set limits (3 prefix, 3 suffix)
  // Existing rare mods are preserved
  item.name = regenerateItemName(item);

  return {
    success: true,
    item,
    message: `Eşya ${randomSet.name} setine dönüştü!`,
    messageKey: 'currency.tamga_muhru.success',
  };
}

/**
 * Tengri Duası - Reroll implicit values on a Legendary item
 */
function applyTengriDuasi(item: EquipmentItem): CurrencyResult {
  if (item.rarity !== 'legendary') {
    return {
      success: false,
      message: 'Tengri Duası sadece Efsanevi eşyalara uygulanabilir',
      messageKey: 'currency.tengri_duasi.error',
    };
  }

  if (!item.implicit || item.implicit.length === 0) {
    return {
      success: false,
      message: 'Eşyanın implicit değeri yok',
      messageKey: 'currency.tengri_duasi.error_no_implicit',
    };
  }

  // Reroll each implicit mod's value within its tier range
  // We vary each value by +/- 20% of the current value (minimum 1)
  item.implicit = item.implicit.map((mod: ItemMod) => {
    const variance = Math.max(1, Math.floor(mod.value * 0.2));
    const minVal = Math.max(1, mod.value - variance);
    const maxVal = mod.value + variance;
    const newValue = minVal + Math.floor(Math.random() * (maxVal - minVal + 1));
    return { ...mod, value: newValue };
  });

  return {
    success: true,
    item,
    message: 'Implicit değerler yeniden atıldı!',
    messageKey: 'currency.tengri_duasi.success',
  };
}

// ========================
// Currency Drop Utilities
// ========================

/**
 * Get total drop weight for currency drop rolling.
 */
export function getTotalCurrencyDropWeight(): number {
  return Object.values(CURRENCY_DEFINITIONS).reduce((sum, def) => sum + def.dropWeight, 0);
}

/**
 * Roll a random currency type based on drop weights.
 */
export function rollCurrencyDrop(): CurrencyType {
  const totalWeight = getTotalCurrencyDropWeight();
  let roll = Math.random() * totalWeight;

  for (const def of Object.values(CURRENCY_DEFINITIONS)) {
    roll -= def.dropWeight;
    if (roll <= 0) {
      return def.id;
    }
  }

  // Fallback to most common
  return 'ruh_tozu';
}

/**
 * Check if a currency can be applied to an item (without applying it).
 */
export function canApplyCurrency(currency: CurrencyType, item: EquipmentItem): boolean {
  switch (currency) {
    case 'ruh_tozu':
      return item.rarity === 'normal';
    case 'tengri_ozu':
      return item.rarity === 'magic';
    case 'erlik_tasi':
      return item.rarity === 'rare';
    case 'arindirma_suyu':
      return item.rarity !== 'normal' && item.rarity !== 'legendary';
    case 'ruh_muhru':
      if (item.rarity !== 'rare') return false;
      return item.prefixes.length < getMaxPrefixes(item.rarity)
        || item.suffixes.length < getMaxSuffixes(item.rarity);
    case 'yikim_tozu':
      return item.rarity === 'rare' && (item.prefixes.length + item.suffixes.length) > 0;
    case 'tamga_muhru':
      return item.rarity === 'rare';
    case 'tengri_duasi':
      return item.rarity === 'legendary' && (item.implicit?.length ?? 0) > 0;
    default:
      return false;
  }
}
